/**
 * Perspectives resource: the current space's perspective list, plus the
 * editorial write.
 *
 * `useSpacePerspectives` reads the space the caller is already inside — every
 * reader (the poll display, the missing-perspectives card, both composer
 * selectors, the editorial edit widget) renders within the question instance,
 * so the space key comes from the instance rather than a parameter.
 * `setSpacePerspectivesAsync` takes one explicitly, because the editorial
 * dashboard seeds a brand-new space from outside it.
 */
import type { PerspectiveInput, PerspectiveResult } from '@repo/shared/orpc/procedures/perspectives';
import type { PerspectiveDraft } from '@repo/shared/perspectives';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { siloParamsOf } from './silo-params';
import { useInstanceQuery } from './use-silo-query';

export type { PerspectiveResult } from '@repo/shared/orpc/procedures/perspectives';
export type { PerspectiveDraft } from '@repo/shared/perspectives';

// One shared empty array, so a still-loading read returns a stable reference
// and doesn't retrigger effects that depend on the list.
const NO_PERSPECTIVES: PerspectiveResult[] = [];

/** The current space's perspective list, in order. Empty while loading. */
export function useSpacePerspectives(): { perspectives: PerspectiveResult[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const query = useInstanceQuery({ procedure: utils.spaces.perspectives.get });
    return { perspectives: query.data?.perspectives ?? NO_PERSPECTIVES, isLoading: query.isLoading };
}

/**
 * Replaces a space's perspective list and invalidates its read.
 *
 * Accepts entries as read (a caller usually has just parsed a textarea, or is
 * reordering what it read back) and sends only the two fields the write takes —
 * `perspectiveKey` is always derived server-side, so passing one back would be
 * silently ignored.
 */
export async function setSpacePerspectivesAsync(
    datastore: Datastore,
    { spaceKey, perspectives }: { spaceKey: string; perspectives: PerspectiveDraft[] },
): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const input: PerspectiveInput[] = perspectives.map(({ emoji, text }) => ({ emoji: emoji ?? null, text }));
    await datastore.getApiClient().spaces.perspectives.set({ siloKey, locale, spaceKey, perspectives: input });
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.spaces.perspectives.get.key({ input: { siloKey, spaceKey } }) }));
}
